import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Eye, Globe, MapPin, Clock, Monitor, Wifi } from 'lucide-react';

const DetailedViewsModal = ({ isOpen, onClose, countryCode = null }) => {
  const [views, setViews] = useState([]);
  const [countryData, setCountryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchDetailedViews();
    }
  }, [isOpen, countryCode]);

  const fetchDetailedViews = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('adminToken');
      const url = countryCode 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/country-detailed-views/${countryCode}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/detailed-views`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des données');
      }

      const data = await response.json();
      
      if (countryCode) {
        setCountryData(data);
        setViews(data.detailed_views);
      } else {
        setViews(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getBrowserInfo = (userAgent) => {
    if (!userAgent || userAgent === 'Unknown') return { browser: '🌐', os: 'Unknown' };
    
    let browser = '🌐';
    let os = 'Unknown';
    
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';
    
    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS';
    
    return { browser, os };
  };

  const getFlagEmoji = (countryCode) => {
    const flagMap = {
      'AU': '🇦🇺', 'US': '🇺🇸', 'GB': '🇬🇧', 'FR': '🇫🇷', 'DE': '🇩🇪',
      'CA': '🇨🇦', 'JP': '🇯🇵', 'IT': '🇮🇹', 'ES': '🇪🇸', 'NL': '🇳🇱',
      'CH': '🇨🇭', 'BE': '🇧🇪', 'SE': '🇸🇪', 'NO': '🇳🇴', 'DK': '🇩🇰'
    };
    return flagMap[countryCode] || '🌍';
  };

  const getUniqueIPs = (viewsArray) => {
    const ipSet = new Set(viewsArray.map(view => view.ip_address));
    return ipSet.size;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        className="bg-slate-900 rounded-3xl border border-white/10 shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center">
                <Eye className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {countryCode ? `Vues détaillées - ${countryData?.country}` : 'Toutes les vues détaillées'}
                </h2>
                <p className="text-blue-100 text-sm">
                  {countryCode ? `${countryData?.total_views} vues • ${countryData?.unique_ips} IPs uniques` : `${views.length} vues totales`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
              <p className="text-gray-400">Chargement des données détaillées...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-400">
              <p>Erreur: {error}</p>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              {countryCode && countryData && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <Eye className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-white">{countryData.total_views}</div>
                        <div className="text-gray-300 text-sm">Vues totales</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <Wifi className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-white">{countryData.unique_ips}</div>
                        <div className="text-gray-300 text-sm">IPs uniques</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-white">{countryData.cities.length}</div>
                        <div className="text-gray-300 text-sm">Villes</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Cities Summary for Country View */}
              {countryCode && countryData?.cities && (
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                    <MapPin className="w-5 h-5 mr-2" />
                    Répartition par ville
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {countryData.cities.map((city, index) => (
                      <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-white">{city.city}</div>
                          <div className="text-sm text-gray-400">{city.total_views} vues</div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {city.unique_ips} IP{city.unique_ips > 1 ? 's' : ''} unique{city.unique_ips > 1 ? 's' : ''}
                        </div>
                        <div className="mt-2 text-xs text-gray-400">
                          {Object.entries(city.ip_breakdown).slice(0, 3).map(([ip, count], i) => (
                            <div key={i}>{ip.replace(/(\d+\.\d+\.\d+\.)\d+/, '$1xxx')}: {count} vues</div>
                          ))}
                          {Object.keys(city.ip_breakdown).length > 3 && (
                            <div>... +{Object.keys(city.ip_breakdown).length - 3} autres IPs</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Views Table */}
              <div>
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  Vues détaillées ({views.length})
                </h3>
                
                <div className="overflow-x-auto">
                  <div className="space-y-2">
                    {views.map((view, index) => {
                      const { browser, os } = getBrowserInfo(view.user_agent);
                      const maskedIP = view.ip_address.replace(/(\d+\.\d+\.\d+\.)\d+/, '$1xxx');
                      
                      return (
                        <motion.div
                          key={view.id}
                          className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-colors"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.02 }}
                        >
                          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
                            {/* IP & Location */}
                            <div className="flex items-center space-x-2">
                              <div className="text-lg">{getFlagEmoji(view.country_code)}</div>
                              <div>
                                <div className="text-white font-medium text-sm">{maskedIP}</div>
                                <div className="text-gray-400 text-xs">{view.city}</div>
                              </div>
                            </div>
                            
                            {/* Country */}
                            <div className="text-gray-300 text-sm">
                              {view.country}
                            </div>
                            
                            {/* Browser & OS */}
                            <div className="flex items-center space-x-1">
                              <Monitor className="w-4 h-4 text-gray-400" />
                              <div className="text-gray-300 text-sm">
                                {browser}<br />
                                <span className="text-xs text-gray-500">{os}</span>
                              </div>
                            </div>
                            
                            {/* Timestamp */}
                            <div className="text-gray-300 text-sm">
                              {formatDate(view.timestamp)}
                            </div>
                            
                            {/* View Number */}
                            <div className="text-center">
                              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                #{views.length - index}
                              </div>
                            </div>
                            
                            {/* Details */}
                            <div className="text-right">
                              <div className="text-xs text-gray-500">
                                Vue #{view.id.slice(-8)}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default DetailedViewsModal;